/**
 * /vs — Index of every competitor comparison page.
 * Linked from the sticky header "Compare" nav item.
 */
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, FileSignature, DollarSign } from "lucide-react";
import { usePageSeo } from "../lib/seo";

const PAGES = [
  {
    slug: "follow-up-boss",
    name: "Follow Up Boss",
    tagline: "Follow Up Boss stops at the contract. iKEY closes it.",
    positioning: "The industry-standard team CRM — but the contract, the client portal, and the AI live in three other subscriptions.",
  },
  {
    slug: "wise-agent",
    name: "Wise Agent",
    tagline: "Wise Agent is a CRM. iKEY is a CRM plus everything an agent uses after the lead.",
    positioning: "Popular budget CRM, but you're still paying for DocuSign, a client portal, and a transaction tracker separately.",
  },
  {
    slug: "kvcore",
    name: "kvCORE",
    tagline: "kvCORE is a brokerage platform. iKEY is a solo-agent workspace.",
    positioning: "Powerful, priced for brokerages, and impossible to sign up for online. iKEY is what a solo agent actually needs.",
  },
  {
    slug: "top-producer",
    name: "Top Producer",
    tagline: "Top Producer runs on paperwork. iKEY runs on AI.",
    positioning: "Long-running incumbent; the UX, the mobile app, and the pricing model all show it.",
  },
];

export default function VsIndex() {
  usePageSeo({
    title: "Compare iKEY — Follow Up Boss, Wise Agent, kvCORE & Top Producer",
    description: "Side-by-side comparisons of iKEY against the four CRMs residential real-estate agents ask about most. Features, pricing, and when to switch.",
    canonical: "https://ikey-app.com/vs",
  });
  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="vs-index-page">
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-slate-200/60">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/ikey-logo.png" alt="iKEY" className="h-9 w-9 rounded-lg ring-1 ring-slate-200" />
            <span className="font-heading text-lg font-bold tracking-tight">iKEY</span>
          </Link>
          <Link to="/" className="text-sm font-semibold text-slate-600 hover:text-slate-900 inline-flex items-center gap-1.5">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
        <div className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: "var(--primary-hex)" }}>Compare</div>
        <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-bold tracking-tighter leading-[1.05]">
          Which CRM should you switch from?
        </h1>
        <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-2xl">
          Feature-by-feature, price-by-price comparisons against the two CRMs individual residential agents ask about most. Both stop where iKEY begins: built-in e-signatures and a real free-forever tier.
        </p>

        <div className="mt-12 flex items-center gap-3 text-sm text-slate-700 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
          <div className="flex items-center gap-2 font-semibold">
            <FileSignature className="h-4 w-4 text-emerald-700" /> DocuSign included on every paid iKEY plan.
          </div>
          <div className="hidden sm:flex items-center gap-2 border-l border-emerald-200 pl-3 font-semibold">
            <DollarSign className="h-4 w-4 text-emerald-700" /> Free forever · first 3 clients.
          </div>
        </div>

        <div className="mt-10 grid gap-5 sm:grid-cols-2">
          {PAGES.map(p => (
            <Link
              key={p.slug}
              to={`/vs/${p.slug}`}
              className="group rounded-2xl ring-1 ring-slate-200 bg-white hover:ring-slate-300 hover:shadow-lg transition-all p-6 flex flex-col"
              data-testid={`vs-index-${p.slug}`}
            >
              <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">iKEY vs.</div>
              <div className="mt-1 font-heading text-2xl font-bold tracking-tight text-slate-900">{p.name}</div>
              <p className="mt-3 text-sm text-slate-700 leading-relaxed flex-1">{p.tagline}</p>
              <p className="mt-3 text-xs text-slate-500 leading-relaxed">{p.positioning}</p>
              <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: "var(--primary-hex)" }}>
                See the side-by-side <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </div>
            </Link>
          ))}
        </div>
      </main>

      <footer className="border-t border-slate-200 py-10">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 text-center text-sm text-slate-500">
          © {new Date().getFullYear()} iKEY, Inc. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
